/**
 * This file is responsible for handling the service calls for the hidden calls
 */
import store from "../store";
import React from "react";
import { callAPI, METHODS, GATEWAY_PROXY } from "utils/apiUtils";
import { DialogHandler } from "cakemail-ui-components/lib/react";
import { Translate } from "react-redux-i18n";
import { getUrlsBasedOnLanguage } from "utils/languages";
import {
	identify,
	SEGMENT_EVENTS,
	trackSegmentEvent,
} from "assets/js/const/SegmentEvents";

/*
=========== ZENDESK SERVICES ===========
 */

export function sendZendeskRequestService(
	options = { content: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/requests",
			body: options.content,
			query: { account_id: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}

export function connectToZendeskSupport(
	options = { isSupport: true, email: undefined },
	successCallback = () => { },
	failureCallback = () => { }
) {
	if (options.isSupport) {
		const state = store.getState();
		const zendeskReturnTo =
			state?.brand?.client_config?.zendesk_config?.return_to;
		const helpUrl = getUrlsBasedOnLanguage(
			state.brand?.url?.help_url,
			"help_url",
			state.global.language
		);

		callAPI(
			{
				url: GATEWAY_PROXY + "/zendesk",
				query: { account_id: store.getState().global?.account?.id || null },
			},
			(data) => {
				if (options.email) {
					identify(options.email);
				}
				trackSegmentEvent(SEGMENT_EVENTS.SUPPORT_SIGNED_IN);

				let url = data?.body?.url;
				if (zendeskReturnTo) {
					url += "&return_to=" + encodeURIComponent(zendeskReturnTo);
				}
				window.open(url, "_blank");
			},
			(msg, error) => {
				if (helpUrl) {
					window.open(helpUrl, "_blank");
				} else {
					DialogHandler.show({
						type: "error",
						title: <Translate value="app-auth-support-error" />,
					});
				}
				failureCallback(msg, error);
			}
		);
	} else {
		callAPI(
			{
				url: GATEWAY_PROXY + "/zendesk",
				query: { account_id: store.getState().global.account.id },
			},
			successCallback,
			failureCallback
		);
	}
}

/*
=========== METADATA SERVICE ===========
 */

export function createAccountMetaDataService(
	options = {
		id: "",
		name: "",
		content: {},
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	const accountId = store.getState().global.account.id;
	callAPI(
		{
			url:
				GATEWAY_PROXY +
				`/accounts/${options.id || accountId}/metadata/${options.name}`,
			body: { data: options.content },
		},
		successCallback,
		failureCallback
	);
}

export function updateAccountMetaDataService(
	options = {
		id: "",
		name: "",
		content: {},
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	const accountId = store.getState().global.account.id;
	callAPI(
		{
			url:
				GATEWAY_PROXY +
				`/accounts/${options.id || accountId}/metadata/${options.name}`,
			body: { data: options.content },
			method: METHODS.put,
			query: { account_id: accountId },
		},
		successCallback,
		failureCallback
	);
}

export function getAccountMetaDataService(
	options = { name: null, id: null },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const accountId = store.getState().global.account.id;
	callAPI(
		{
			url:
				GATEWAY_PROXY +
				`/accounts/${options.id || accountId}/metadata/${options.name}`,
			method: METHODS.get,
			query: { account_id: accountId },
		},
		successCallback,
		failureCallback
	);
}

export function createUserMetaDataService(
	options = {
		id: "",
		name: "",
		content: {},
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/users/${options.id}/metadata/${options.name}`,
			body: { data: options.content },
			query: { account_id: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}

export function getUserMetaDataService(
	options = { name: null, id: null },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/users/${options.id}/metadata/${options.name}`,
			method: METHODS.get,
		},
		successCallback,
		failureCallback
	);
}

export function deleteUserMetaDataService(
	options = { name: null, id: null },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/users/${options.id}/metadata/${options.name}`,
			method: METHODS.delete,
		},
		successCallback,
		failureCallback
	);
}


export function updateUserMetaDataService(
	options = {
		id: "",
		name: "",
		content: {},
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/users/${options.id}/metadata/${options.name}`,
			body: { data: options.content },
			method: METHODS.put,
			query: { account_id: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}

/*
=========== TOKEN SERVICE ===========
 */
export function getBeeTokenService(
	options = { editorType: "email_designer" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { editorType } = options;
	callAPI(
		{
			url: GATEWAY_PROXY + "/bee/token",
			method: METHODS.post,
			body: {
				type: "email_designer", //"pop_up_designer", "form_designer", "email_designer"
			},
		},
		successCallback,
		failureCallback
	);
}

/*
=========== DESIGNER ROWS SERVICE ===========
 */
export function getDesignerRowsService(
	options = {},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/designerRows",
			method: METHODS.get,
			query: { accountId: store.getState().global.account.id, ...options },
		},
		successCallback,
		failureCallback
	);
}

export function getDesignerBlueprintRowsService(
	options = {},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/designerRows/blueprints",
			method: METHODS.get,
			query: { accountId: store.getState().global.account.id, ...options },
		},
		successCallback,
		failureCallback
	);
}

export function createDesignerRowService(
	options = { row: {} },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/designerRows",
			method: METHODS.post,
			query: { accountId: store.getState().global.account.id },
			body: options.row,
		},
		successCallback,
		failureCallback
	);
}

export function deleteDesignerRowService(
	options = { id: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/designerRows/" + options.id,
			method: METHODS.delete,
			query: { accountId: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}

export function updateDesignerRowService(
	options = { metadata: {} },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/designerRows/" + options.id,
			method: METHODS.put,
			body: options,
			query: { accountId: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}

/*
=========== AI GENERATOR SERVICE ===========
 */

export function aiGeneratorService(
	options = {
		email: "",
		subject: "",
		description: "",
		tone: "",
		purpose: "",
		stringInclude: "",
		number: 3,
		contentFormat: "none",
		generationType: "subject",
	},
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { generationType, ...restOptions } = options;

	if (!options.number) {
		options.number = 3;
	}

	callAPI(
		{
			url: GATEWAY_PROXY + "/ai/subjectlines",
			method: METHODS.post,
			body: restOptions,
			query: { accountId: store.getState().global.account.id },
		},
		(data) => {
			const eventContext = { ...restOptions };
			delete eventContext.email;
			trackSegmentEvent(SEGMENT_EVENTS.AI_SUBJECT_GENERATED, {
				generated: generationType,
				context: eventContext,
			});
			successCallback(data);
		},
		failureCallback
	);
}

export function applyBrandToBeeService(
	options = { json: {} },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/smartTemplate/applyBrand",
			method: METHODS.post,
			body: options,
			query: { accountId: store.getState().global.account.id },
		},
		successCallback,
		failureCallback
	);
}


/*
=========== New billing service ===========
 */

export function createBillingTokenService(
	options = {},
	successCallback = () => { },
	failureCallback = () => { }
) {
	let localToken = null;
	try {
		localToken = JSON.parse(window.localStorage.getItem("billingToken"));
	} catch (e) { }

	if (localToken?.expires > Date.now()) {
		successCallback({
			token: localToken.token
		});
	} else {
		callAPI(
			{
				url: GATEWAY_PROXY + "/billing/token",
				method: METHODS.post,
				body: {
					partnerUrl: window.location.host
				}
			},
			(data) => {
				try {
					//expires 1h - 2m later
					window.localStorage.setItem("billingToken", JSON.stringify({
						token: data.token,
						expires: Date.now() + 3300000
					}));
				} catch (e) { }

				successCallback(data);
			},
			failureCallback
		);
	}
}


export function applyBillingLimitsService(
	options = { token: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: process.env.BILLING_URL_BE + "/admin/applyLimits",
			method: METHODS.post,
			body: {
				token: options.token
			}
		},
		successCallback,
		failureCallback
	);
}